function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import "core-js/modules/es.function.name.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.set.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.symbol.iterator.js";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.array.from.js";
import "core-js/modules/es.regexp.exec.js";
import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import React from 'react';
import { QosErrorBoundary } from './QosErrorBoundary';
import { QosContext } from './QosContext';
import { createQosTelemetryReporter } from './createQosTelemetryReporter';
import { QosState } from './types';
import { useQos } from '.';
import { useTelemetry } from '..';
/**
 * Timestamp for QosProvider to timeout
 */

export var timeoutMs = 30000;

/**
 * Wrapper component that wraps the target UI to track its QoS. The target UI can be a single component or collection of components
 */
export var QosProvider = function QosProvider(_ref) {
  var children = _ref.children,
      qosProviderName = _ref.name,
      _ref$timeout = _ref.timeout,
      timeout = _ref$timeout === void 0 ? timeoutMs : _ref$timeout,
      onQosStateChange = _ref.onQosStateChange;
  var incompleteComponents = useRef(new Set());

  var _useState = useState(QosState.IDLE),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      componentCount = _useState4[0],
      setComponentCount = _useState4[1];

  var telemetry = useTelemetry(); // Get access to the context from the parent QosProvider because the current QosProvider is also a child of its parent, so it needs to report its own QoS status.
  // If this is the outermost QosProvider i.e. there is no context from the parent, then the returned callbacks are no-op.

  var parentQosContext = useQos(qosProviderName, {
    disableWithoutContext: true
  });
  var qosTelemetryReporter = useMemo(function () {
    return createQosTelemetryReporter(telemetry, qosProviderName);
  }, [telemetry, qosProviderName]);
  var currentQosContext = useMemo(function () {
    function unregister(child) {
      incompleteComponents.current.delete(child);
      setComponentCount(incompleteComponents.current.size);
    }

    return {
      name: qosProviderName,
      unregisterComponent: function unregisterComponent(child) {
        unregister(child);
      },
      registerComponent: function registerComponent(child) {
        incompleteComponents.current.add(child);
        setComponentCount(incompleteComponents.current.size);
      },
      reportLoadComplete: function reportLoadComplete(child) {
        unregister(child);
      },
      reportFailure: function reportFailure(child, error) {
        unregister(child);
        qosTelemetryReporter.failure(error, "".concat(qosProviderName, "/").concat(child.componentName));
        setState(qosTelemetryReporter.state);
      }
    };
  }, [qosTelemetryReporter, qosProviderName]);
  useEffect(function () {
    if (incompleteComponents.current.size === 0) {
      // Send Qos log
      qosTelemetryReporter.complete();
      setState(qosTelemetryReporter.state); // Reports complete to the parent QosProvider

      parentQosContext && parentQosContext.reportLoadComplete();
    }
  }, [qosTelemetryReporter, componentCount, parentQosContext]); // Timeout after `timeout` milliseconds have elapsed. The timeout will reset
  // if the QoS context or duration changes.

  useEffect(function () {
    var timeoutCreator = setTimeout(function () {
      if (incompleteComponents.current.size > 0) {
        qosTelemetryReporter.timeout();
        setState(qosTelemetryReporter.state); // Always report complete to the parent as timeout and failure only affect the current QosProvider

        parentQosContext && parentQosContext.reportLoadComplete();
      }
    }, timeout);
    return function () {
      return clearTimeout(timeoutCreator);
    };
  }, [qosTelemetryReporter, parentQosContext, timeout]);
  useEffect(function () {
    if (state !== QosState.IDLE) {
      onQosStateChange === null || onQosStateChange === void 0 ? void 0 : onQosStateChange(state);
    }
  }, [state, onQosStateChange]);
  var onCatchError = useCallback(function (error) {
    // Send QoS log immediately so we won't lose it before the QosProvider unmounts
    qosTelemetryReporter.failure(error);
    setState(qosTelemetryReporter.state);
  }, [qosTelemetryReporter]);
  return /*#__PURE__*/React.createElement(QosContext.Provider, {
    value: currentQosContext
  }, /*#__PURE__*/React.createElement(QosErrorBoundary, {
    onCatch: onCatchError
  }, children));
};
//# sourceMappingURL=QosProvider.js.map